import {ListRules} from "./components";
import {useTelegramHideBackButton} from "@hooks/telegram.ts";
import React from "react";
import {useAutoRetry, useRuleList} from "@hooks";
import {Skeleton} from "@components/ui/skeleton.tsx";
import {resolveVenue, RuleType, VenueType} from "@types";
import {useParams} from "react-router-dom";


export const AutomatePage = React.memo(() => {
    useTelegramHideBackButton();

    const {venue: venueParam} = useParams();
    const venue = resolveVenue(venueParam);
    if (!venue) {
        return (<h1>Invalid venue</h1>);
    }

    if (venue !== VenueType.PUMP_FUN && venue !== VenueType.PUMP_UP) {
        return (
            <div className="text-orange-200 text-xl backdrop-blur-xl text-center">
                <h1>🚧 Nyanbot is busy upgrading! 🚀</h1>
                <p>We’re adding more magic to your trading experience. Check back soon for exciting new features! 🌈✨</p>
            </div>
        );
    }

    const [rules, loading, error, reload] = useRuleList();
    const MAX_ATTEMPTS = 5;

    const [retryCount, retrying, secondsRemaining, resetRetry] = useAutoRetry({
        shouldRetry: !rules && !!error,
        maxAttempts: MAX_ATTEMPTS,
        retryCallback: reload,
    });

    if (!rules || loading || retrying) {
        return (
            <div className="pt-10 flex flex-col items-center space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                    {Array(6).fill(null).map((_, index) => (
                        <Skeleton key={index} className="h-16 w-full rounded-lg"/>
                    ))}
                </div>
                {retrying && (
                    <p className="text-gray-500 text-md">
                        Failed to load. Trying again ({retryCount + 1}/ {MAX_ATTEMPTS})...
                    </p>
                )}

                {retrying && secondsRemaining > 0 && (
                    <p className="text-gray-500 text-sm">
                        Retrying in {secondsRemaining}s
                    </p>
                )}
            </div>
        );
    }

    if (error && retryCount >= MAX_ATTEMPTS) {
        return (
            <div className="pt-32 flex flex-col space-y-6 items-center justify-center text-center">
                <p className="text-red-500">Failed to load rules after {MAX_ATTEMPTS} attempts.</p>
                <button
                    onClick={() => {
                        resetRetry();
                        reload();
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
                >
                    Try again
                </button>
            </div>
        );
    }

    const ruleType = resolveRuleType(venue);
    const managedRuleType = resolveManagedRuleType(venue);
    const userRules = rules.filter((r) => r.rule === ruleType);
    const managedRules = rules.filter((r) => r.rule === managedRuleType);

    return (
        <div className="py-10 px-6 space-y-12">
            <div className="max-w-5xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-orange-500 tracking-tight drop-shadow-sm">
                        <UserRuleTitle venue={venue}/>
                    </h1>
                </div>

                <ListRules rules={userRules} ruleType={ruleType}/>
            </div>


            <div className="max-w-5xl mx-auto space-y-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-orange-500 tracking-tight drop-shadow-sm">
                        <ManagedRuleTitle venue={venue}/>
                    </h1>
                </div>

                <ListRules rules={managedRules} ruleType={managedRuleType}/>
            </div>
        </div>
    );
});

const resolveRuleType = (venue: VenueType): RuleType => {
    switch (venue) {
        case VenueType.PUMP_FUN:
            return RuleType.PUMP_FUN;
        case VenueType.PUMP_SWAP:
            return RuleType.PUMP_SWAP;
        case VenueType.PUMP_UP:
            return RuleType.PUMP_UP;
        case VenueType.JUPITER:
            return RuleType.JUPITER;
    }
}

const resolveManagedRuleType = (venue: VenueType): RuleType => {
    switch (venue) {
        case VenueType.PUMP_FUN:
            return RuleType.PUMP_FUN_MANAGED;
        case VenueType.PUMP_SWAP:
            return RuleType.PUMP_SWAP_MANAGED;
        case VenueType.PUMP_UP:
            return RuleType.PUMP_UP_MANAGED;
        case VenueType.JUPITER:
            return RuleType.JUPITER_MANAGED;
    }
}


const UserRuleTitle = ({venue}: { venue: VenueType }) => {
    switch (venue) {
        case VenueType.PUMP_FUN:
            return (<h1>Your pump.fun rules</h1>)
        case VenueType.JUPITER:
            return (<h1>Your Jupiter rules</h1>)
        case VenueType.PUMP_SWAP:
            return (<h1>Your pump swap rules</h1>)
        case VenueType.PUMP_UP:
            return (<h1>Your Pumpup.ai rules</h1>)
    }

}

const ManagedRuleTitle = ({venue}: { venue: VenueType }) => {
    switch (venue) {
        case VenueType.PUMP_FUN:
            return (<h1>AI managed pump.fun rules</h1>)
        case VenueType.JUPITER:
            return (<h1>AI managed Jupiter rules</h1>)
        case VenueType.PUMP_SWAP:
            return (<h1>AI managed pump swap rules</h1>)
        case VenueType.PUMP_UP:
            return (<h1>AI managed Pumpup.ai rules</h1>)
    }

}